// ABOUTME: Smart wrapper for the Delivery Rate (load spacing) entity — owns useLoadSpacingEntity and reports state via context.
// ABOUTME: Renders skeleton/error/form; computes UoM options from catalog; controls section visibility via scenarios flags.
import React, {useMemo} from 'react'

import {useTranslation} from '../../../../hooks/useTranslation'
import {useEntityFormContext, useRegisterEntityForm} from '../context/EntityFormContext'
import {useLoadSpacingEntity} from '../hooks/useLoadSpacingEntity'
import {LoadSpacingEntityOptions, UnitOfMeasureRef} from '../types'
import {EntityContentEmpty} from './EntityContentEmpty'
import {EntityContentError} from './EntityContentError'
import {EntityContentSkeleton} from './EntityContentSkeleton'
import {DeliveryRateForm} from './DeliveryRateForm'

const toUomOpts = (refs: UnitOfMeasureRef[] | undefined) =>
  (refs ?? []).map((r) => ({value: r.id, label: r.name || r.displaySymbol, symbol: r.displaySymbol || r.name}))

export const DeliveryRateEntityForm: React.FC = () => {
  const {t} = useTranslation()
  const ctx = useEntityFormContext()
  const opts = ctx.loadSpacingOptions

  const {config, isLoading, error, isSaving, actions} = useLoadSpacingEntity({
    countryId: ctx.countryId,
    businessLine: ctx.businessLine,
    scopeId: ctx.selectedScopeId,
    pendingScope: ctx.pendingScope,
    rootScopeId: ctx.rootScopeId,
  })

  useRegisterEntityForm(actions, isSaving)

  const timeOnJobSiteUomOptions = useMemo(() => toUomOpts(opts?.availableUnitsOfMeasure?.timeOnJobSite), [opts])
  const spacingUomOptions = useMemo(() => toUomOpts(opts?.availableUnitsOfMeasure?.spacing), [opts])
  const flowRateUomOptions = useMemo(() => toUomOpts(opts?.availableUnitsOfMeasure?.flowRate), [opts])

  const showTimeOnJobSite = Boolean(opts?.scenarios?.singleTruck?.timeOnJobSite || opts?.scenarios?.multiTruck?.timeOnJobSite)
  const showSpacing = Boolean(opts?.scenarios?.singleTruck?.spacing || opts?.scenarios?.multiTruck?.spacing)
  const showFlowRate = Boolean(opts?.scenarios?.singleTruck?.flowRate || opts?.scenarios?.multiTruck?.flowRate)
  const showStockpiling = Boolean(opts?.stockpilingEnabled)

  if (isLoading) return <EntityContentSkeleton />
  if (error) return <EntityContentError />
  if (!config) return <EntityContentEmpty />

  return (
    <DeliveryRateForm
      key={ctx.formResetKey}
      config={config}
      isReadOnly={ctx.isReadOnly}
      onDirtyChange={ctx.onDirtyChange}
      onValidChange={ctx.onValidChange}
      onValuesChange={actions.handleValuesChange}
      timeOnJobSiteUomOptions={timeOnJobSiteUomOptions}
      spacingUomOptions={spacingUomOptions}
      flowRateUomOptions={flowRateUomOptions}
      showTimeOnJobSite={showTimeOnJobSite}
      showSpacing={showSpacing}
      showFlowRate={showFlowRate}
      showStockpiling={showStockpiling}
      isRoot={ctx.isCountryDefault}
    />
  )
}
